// Product analytics: one provider-neutral track() that fans out to
//   - Umami (self-hosted, cookieless): always, when the tracker script is present
//   - Google Analytics: only when the visitor's stored cookie consent has analytics: true
// Event names and props are defined in utils/analytics-events.ts. Never throws; a missing tracker
// or blocked storage is silently ignored so analytics can never break the product.
import { isEventName, type EventName, type TrackProps } from './analytics-events';
export type { EventName, TrackProps } from './analytics-events';
export { EVENTS, MAIN_FUNNEL, FREE_COVER_FUNNEL } from './analytics-events';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    umami?: { track: (name: string, data?: Record<string, unknown>) => void };
  }
}

export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID || '';

/** True when GA is configured and its tag has loaded. Evaluated per call (the tag loads late). */
export function isGAEnabled(): boolean {
  return typeof window !== 'undefined' && !!GA_TRACKING_ID && typeof window.gtag === 'function';
}

/** Whether the visitor accepted analytics cookies (components/common/CookieConsent.tsx). */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return false;
    return JSON.parse(raw)?.analytics === true;
  } catch {
    return false;
  }
}

// Umami loads afterInteractive, so events fired on mount can precede it. Queue them (bounded) and
// flush in order once window.umami exists; give up after UMAMI_WAIT_MS and drop the queue.
const QUEUE_MAX = 50;
const POLL_MS = 250;
const UMAMI_WAIT_MS = 15000;
let umamiQueue: Array<[string, Record<string, unknown>]> = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPolling() {
  if (pollTimer !== null) clearInterval(pollTimer);
  pollTimer = null;
}

function flushUmami(): boolean {
  if (typeof window === 'undefined' || !window.umami) return false;
  const q = umamiQueue;
  umamiQueue = [];
  for (const [n, p] of q) {
    try { window.umami.track(n, p); } catch { /* ignore */ }
  }
  return true;
}

function startPolling() {
  if (pollTimer !== null) return;
  const startedAt = Date.now();
  pollTimer = setInterval(() => {
    if (flushUmami() || Date.now() - startedAt >= UMAMI_WAIT_MS) {
      umamiQueue = [];
      stopPolling();
    }
  }, POLL_MS);
}

function sendUmami(name: string, props: Record<string, unknown>) {
  if (window.umami) {
    flushUmami(); // anything queued earlier goes first
    window.umami.track(name, props);
    stopPolling();
    return;
  }
  umamiQueue.push([name, props]);
  if (umamiQueue.length > QUEUE_MAX) umamiQueue.shift();
  startPolling();
}

/** Product event. Umami always; GA only with consent. Safe to call anywhere, including SSR. */
export function track(name: EventName, props: TrackProps = {}): void {
  if (typeof window === 'undefined') return;
  try {
    sendUmami(name, props as Record<string, unknown>);
  } catch {
    /* tracker missing or blocked */
  }
  try {
    if (isGAEnabled() && hasAnalyticsConsent()) window.gtag('event', name, props);
  } catch {
    /* ignore */
  }
}

// GA page views (route changes). Umami tracks page views itself.
export const pageview = (url: string) => {
  if (!isGAEnabled() || !hasAnalyticsConsent()) return;
  window.gtag('config', GA_TRACKING_ID, { page_path: url });
};

// Legacy GA-shaped event helper kept for existing call sites. Known product events are routed
// through track(); anything else goes to GA only (with consent) as before.
export const event = ({ action, category, label, value }: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) => {
  if (isEventName(action)) {
    track(action, { source: label, value });
    return;
  }
  if (!isGAEnabled() || !hasAnalyticsConsent()) return;
  window.gtag('event', action, { event_category: category, event_label: label, value });
};

// Social sharing
export const trackSocialShare = (platform: string, bookId?: string, bookTitle?: string) => {
  track('share_clicked', { platform, book_id: bookId });
  event({ action: 'share', category: 'social', label: platform });
  event({ action: `share_${platform}`, category: 'social_sharing', label: bookId || 'general' });
  if (bookId) {
    event({ action: 'book_shared', category: 'engagement', label: `${platform}_${bookId}`, value: 1 });
  }
};

export const trackCopyLink = (bookId?: string) => {
  track('copy_link', { book_id: bookId });
};

export const trackShareMenuOpen = (source: 'inline' | 'floating') => {
  track('share_menu_open', { source });
};

// Book creation steps (legacy GA-only signal)
export const trackBookCreationStep = (step: string, details?: any) => {
  event({ action: 'book_creation_step', category: 'book_creation', label: step });
};

// Preview page views
export const trackBookPreview = (bookId: string, isMultiCharacter: boolean = false) => {
  track('preview_viewed', { book_id: bookId });
  event({
    action: 'book_preview_viewed',
    category: 'book_preview',
    label: isMultiCharacter ? 'multi_character' : 'single_character',
  });
};

// Checkout intent (from preview or share page)
export const trackCheckoutIntent = (bookId: string, fromPreview: boolean = true) => {
  event({ action: 'checkout_intent', category: 'conversion', label: fromPreview ? 'from_preview' : 'from_share' });
};
